import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, MoreHorizontal, RefreshCw, Loader2, X, Link, Phone, MessageSquareReply,
  Eye, Pencil, Trash2, AlertTriangle, Copy, Send,
} from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { FilterBar } from "@/components/shared/FilterBar";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ApiTemplate {
  id: number;
  meta_template_id: string | null;
  name: string;
  language: string;
  category: string;
  status: string;
  components: Array<Record<string, unknown>>;
  quality_score: string | null;
  rejection_reason: string | null;
  body_text: string | null;
  variable_count: number;
  waba_name: string;
  created_at: string;
  updated_at: string;
}

interface TemplateCounts {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
}

interface Waba {
  id: number;
  waba_id: string;
  name: string;
}

type HeaderType = "none" | "text" | "image" | "video" | "document";
type ButtonType = "QUICK_REPLY" | "URL" | "PHONE_NUMBER";
interface TemplateButton {
  type: ButtonType;
  text: string;
  url?: string;
  phone_number?: string;
  payload?: string;
}

const CATEGORIES = [
  { value: "UTILITY", label: "Utility" },
  { value: "MARKETING", label: "Marketing" },
  { value: "AUTHENTICATION", label: "Authentication" },
];

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "en_US", label: "English (US)" },
  { value: "es", label: "Spanish" },
  { value: "es_MX", label: "Spanish (Mexico)" },
  { value: "es_AR", label: "Spanish (Argentina)" },
  { value: "pt_BR", label: "Portuguese (BR)" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "zh_CN", label: "Chinese (Simplified)" },
  { value: "ar", label: "Arabic" },
  { value: "hi", label: "Hindi" },
];

const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const selectClass = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const labelClass = "mb-1 block text-xs font-medium text-muted-foreground";

function extractVariables(text: string): string[] {
  const matches = text.match(/\{\{\d+\}\}/g);
  if (!matches) return [];
  return [...new Set(matches)].sort();
}

function buildComponents(
  headerType: HeaderType,
  headerText: string,
  bodyText: string,
  footerText: string,
  buttons: TemplateButton[],
  variableExamples: Record<string, string>,
) {
  const components: Record<string, unknown>[] = [];

  if (headerType === "text" && headerText.trim()) {
    const headerComp: Record<string, unknown> = { type: "HEADER", format: "TEXT", text: headerText.trim() };
    const headerVars = extractVariables(headerText);
    if (headerVars.length > 0) {
      headerComp.example = {
        header_text: headerVars.map((v) => variableExamples[`header_${v}`] || "Example"),
      };
    }
    components.push(headerComp);
  } else if (headerType !== "none" && headerType !== "text") {
    components.push({ type: "HEADER", format: headerType.toUpperCase() });
  }

  if (bodyText.trim()) {
    const bodyComp: Record<string, unknown> = { type: "BODY", text: bodyText.trim() };
    const bodyVars = extractVariables(bodyText);
    if (bodyVars.length > 0) {
      bodyComp.example = {
        body_text: [bodyVars.map((v) => variableExamples[`body_${v}`] || "Example")],
      };
    }
    components.push(bodyComp);
  }

  if (footerText.trim()) {
    components.push({ type: "FOOTER", text: footerText.trim() });
  }

  if (buttons.length > 0) {
    const metaButtons = buttons.map((b) => {
      if (b.type === "QUICK_REPLY") {
        const qr: Record<string, string> = { type: "QUICK_REPLY", text: b.text };
        if (b.payload) qr.payload = b.payload;
        return qr;
      }
      if (b.type === "URL") return { type: "URL", text: b.text, url: b.url || "" };
      return { type: "PHONE_NUMBER", text: b.text, phone_number: b.phone_number || "" };
    });
    components.push({ type: "BUTTONS", buttons: metaButtons });
  }

  return components;
}

function parseComponentsToForm(components: Array<Record<string, unknown>>) {
  let headerType: HeaderType = "none";
  let headerText = "";
  let bodyText = "";
  let footerText = "";
  const buttons: TemplateButton[] = [];
  const variableExamples: Record<string, string> = {};

  for (const c of components) {
    if (c.type === "HEADER") {
      if (c.format === "TEXT") {
        headerType = "text";
        headerText = (c.text as string) || "";
        const examples = (c as any).example?.header_text as string[] | undefined;
        if (examples) {
          extractVariables(headerText).forEach((v, i) => {
            variableExamples[`header_${v}`] = examples[i] || "";
          });
        }
      } else {
        headerType = ((c.format as string) || "image").toLowerCase() as HeaderType;
      }
    } else if (c.type === "BODY") {
      bodyText = (c.text as string) || "";
      const examples = (c as any).example?.body_text?.[0] as string[] | undefined;
      if (examples) {
        extractVariables(bodyText).forEach((v, i) => {
          variableExamples[`body_${v}`] = examples[i] || "";
        });
      }
    } else if (c.type === "FOOTER") {
      footerText = (c.text as string) || "";
    } else if (c.type === "BUTTONS") {
      const btns = (c as any).buttons as Array<Record<string, string>> | undefined;
      if (btns) {
        for (const b of btns) {
          buttons.push({
            type: b.type as ButtonType,
            text: b.text || "",
            url: b.url,
            phone_number: b.phone_number,
            payload: b.payload,
          });
        }
      }
    }
  }

  return { headerType, headerText, bodyText, footerText, buttons, variableExamples };
}

function WhatsAppPreview({
  headerType, headerText, bodyText, footerText, buttons, variableExamples,
}: {
  headerType: HeaderType;
  headerText: string;
  bodyText: string;
  footerText: string;
  buttons: TemplateButton[];
  variableExamples?: Record<string, string>;
}) {
  const bodyVars = extractVariables(bodyText);
  let previewBody = bodyText;
  bodyVars.forEach((v) => {
    const example = variableExamples?.[`body_${v}`];
    previewBody = previewBody.replace(v, example || `[${v}]`);
  });

  const headerVars = extractVariables(headerText);
  let previewHeader = headerText;
  headerVars.forEach((v) => {
    const example = variableExamples?.[`header_${v}`];
    previewHeader = previewHeader.replace(v, example || `[${v}]`);
  });

  return (
    <div className="rounded-xl bg-[#ECE5DD] p-6">
      <div className="mx-auto max-w-xs space-y-0.5">
        <div className="rounded-lg rounded-tl-none bg-white p-3 text-xs shadow-sm">
          {headerType === "text" && previewHeader.trim() && (
            <div className="mb-1 font-semibold">{previewHeader}</div>
          )}
          {headerType === "image" && (
            <div className="mb-2 flex h-32 items-center justify-center rounded bg-muted text-xs text-muted-foreground">Image</div>
          )}
          {headerType === "video" && (
            <div className="mb-2 flex h-32 items-center justify-center rounded bg-muted text-xs text-muted-foreground">Video</div>
          )}
          {headerType === "document" && (
            <div className="mb-2 flex h-10 items-center justify-center rounded bg-muted text-xs text-muted-foreground">Document</div>
          )}
          {previewBody.trim() && (
            <p className="whitespace-pre-wrap text-foreground/80">{previewBody}</p>
          )}
          {footerText.trim() && (
            <p className="mt-2 text-[10px] text-muted-foreground">{footerText}</p>
          )}
        </div>
        {buttons.length > 0 && (
          <div className="space-y-0.5">
            {buttons.map((b, i) => (
              <div key={i} className="flex items-center justify-center gap-1.5 rounded-lg bg-white py-2 text-xs font-medium text-[#0D94F5] shadow-sm">
                {b.type === "URL" && <Link className="h-3 w-3" />}
                {b.type === "PHONE_NUMBER" && <Phone className="h-3 w-3" />}
                {b.type === "QUICK_REPLY" && <MessageSquareReply className="h-3 w-3" />}
                {b.text || "Button"}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Actions dropdown ---
function ActionsMenu({
  template,
  onView,
  onEdit,
  onDelete,
  onDuplicate,
}: {
  template: ApiTemplate;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="icon" aria-label="Actions" onClick={() => setOpen(!open)}>
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-40 rounded-md border border-border bg-popover py-1 shadow-lg">
          <button onClick={() => { setOpen(false); onView(); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted">
            <Eye className="h-3.5 w-3.5" /> View details
          </button>
          <button onClick={() => { setOpen(false); onEdit(); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
          <button onClick={() => { setOpen(false); onDuplicate(); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted">
            <Copy className="h-3.5 w-3.5" /> Duplicate
          </button>
          <div className="my-1 border-t border-border" />
          <button onClick={() => { setOpen(false); onDelete(); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-destructive hover:bg-muted">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

// --- Detail drawer ---
function TemplateDetailDrawer({ template, onClose, onEdit }: { template: ApiTemplate; onClose: () => void; onEdit: () => void }) {
  const parsed = parseComponentsToForm(template.components);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground/30" />
      <div onClick={(e) => e.stopPropagation()} className="relative h-full w-full max-w-lg overflow-y-auto bg-card shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <div>
            <h3 className="font-semibold">{template.name}</h3>
            <p className="text-xs text-muted-foreground font-mono">{template.meta_template_id || `ID: ${template.id}`}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-6 p-6">
          {/* Status + rejection reason */}
          <div className="flex items-start gap-3">
            <StatusBadge status={template.status} />
            {template.quality_score && (
              <span className="text-xs text-muted-foreground">Quality: {template.quality_score}</span>
            )}
          </div>

          {template.status === "rejected" && template.rejection_reason && (
            <div className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
              <div>
                <p className="text-xs font-medium text-destructive">Rejection reason</p>
                <p className="mt-0.5 text-xs text-foreground/80">{template.rejection_reason}</p>
              </div>
            </div>
          )}

          {/* Details table */}
          <div className="rounded-md border border-border divide-y divide-border text-xs">
            <div className="flex justify-between px-3 py-2">
              <span className="text-muted-foreground">Category</span>
              <span className="font-medium">{template.category}</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-muted-foreground">Language</span>
              <span className="font-medium">{LANGUAGES.find((l) => l.value === template.language)?.label || template.language}</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-muted-foreground">WABA</span>
              <span className="font-medium">{template.waba_name}</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-muted-foreground">Variables</span>
              <span className="font-medium">{template.variable_count}</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-muted-foreground">Created</span>
              <span className="font-medium">{new Date(template.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-muted-foreground">Modified</span>
              <span className="font-medium">{new Date(template.updated_at).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Preview */}
          <div>
            <h4 className="mb-2 text-xs font-medium text-muted-foreground">Preview</h4>
            <WhatsAppPreview
              headerType={parsed.headerType}
              headerText={parsed.headerText}
              bodyText={parsed.bodyText}
              footerText={parsed.footerText}
              buttons={parsed.buttons}
              variableExamples={parsed.variableExamples}
            />
          </div>

          {/* Raw components */}
          <div>
            <h4 className="mb-2 text-xs font-medium text-muted-foreground">Components (raw)</h4>
            <pre className="max-h-60 overflow-auto rounded-md border border-border bg-muted/30 p-3 text-[11px] leading-relaxed">
              {JSON.stringify(template.components, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Delete confirmation ---
function DeleteConfirm({
  template,
  onClose,
  onConfirm,
  isPending,
}: {
  template: ApiTemplate;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground/30" />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10">
            <Trash2 className="h-4 w-4 text-destructive" />
          </div>
          <div>
            <h3 className="font-semibold">Delete template</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Are you sure you want to delete <span className="font-mono font-medium text-foreground">{template.name}</span>?
              {template.meta_template_id && " This will also delete it from Meta."}
              {" "}This action cannot be undone.
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={onConfirm} disabled={isPending}>
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

// --- Template wizard (create + edit) ---
function TemplateWizard({
  onClose,
  editTemplate,
}: {
  onClose: () => void;
  editTemplate?: ApiTemplate | null;
}) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(editTemplate ? 2 : 1);
  const isEdit = !!editTemplate;

  // Step 1
  const [wabaId, setWabaId] = useState(editTemplate ? "" : "");
  const [templateName, setTemplateName] = useState(editTemplate?.name || "");
  const [category, setCategory] = useState(editTemplate?.category || "");
  const [language, setLanguage] = useState(editTemplate?.language || "es");

  // Step 2
  const parsedEdit = editTemplate ? parseComponentsToForm(editTemplate.components) : null;
  const [headerType, setHeaderType] = useState<HeaderType>(parsedEdit?.headerType || "none");
  const [headerText, setHeaderText] = useState(parsedEdit?.headerText || "");
  const [bodyText, setBodyText] = useState(parsedEdit?.bodyText || "");
  const [footerText, setFooterText] = useState(parsedEdit?.footerText || "");
  const [buttons, setButtons] = useState<TemplateButton[]>(parsedEdit?.buttons || []);
  const [variableExamples, setVariableExamples] = useState<Record<string, string>>(parsedEdit?.variableExamples || {});

  const { data: wabasData } = useQuery({
    queryKey: ["wabas"],
    queryFn: () => apiFetch<{ wabas: Waba[] }>("/api/wabas"),
  });

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiFetch("/api/templates", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      onClose();
    },
    onError: (err: Error) => {
      alert(`Failed to create template: ${err.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiFetch(`/api/templates/${editTemplate?.id}`, { method: "PATCH", body: JSON.stringify(payload) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      onClose();
    },
    onError: (err: Error) => {
      alert(`Failed to update template: ${err.message}`);
    },
  });

  const wabas = wabasData?.wabas ?? [];
  const step1Valid = (isEdit || (wabaId && templateName && category && language));
  const bodyVars = extractVariables(bodyText);
  const headerVars = extractVariables(headerText);
  const allVars = [
    ...bodyVars.map((v) => ({ key: `body_${v}`, label: `Body ${v}`, variable: v })),
    ...headerVars.map((v) => ({ key: `header_${v}`, label: `Header ${v}`, variable: v })),
  ];
  const step2Valid = bodyText.trim().length > 0;
  const step3Valid = allVars.length === 0 || allVars.every((v) => (variableExamples[v.key] || "").trim().length > 0);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const addButton = (type: ButtonType) => {
    if (buttons.length >= 3) return;
    setButtons([...buttons, { type, text: "" }]);
  };

  const updateButton = (index: number, field: string, value: string) => {
    setButtons(buttons.map((b, i) => (i === index ? { ...b, [field]: value } : b)));
  };

  const removeButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const setExample = (key: string, value: string) => {
    setVariableExamples((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (submit: boolean) => {
    const components = buildComponents(headerType, headerText, bodyText, footerText, buttons, variableExamples);
    if (isEdit) {
      updateMutation.mutate({ components, category, language, submit });
    } else {
      createMutation.mutate({
        waba_id: wabaId,
        name: templateName,
        category,
        language,
        components,
        submit,
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground/30" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        style={{ maxHeight: "90vh" }}
      >
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-base font-semibold">{isEdit ? `Edit: ${editTemplate.name}` : "Create message template"}</h3>
          <div className="mt-3 flex items-center gap-2">
            {[1, 2, 3].map((n) => (
              <div key={n} className="flex flex-1 items-center gap-2">
                <div className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                  step >= n ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                )}>{n}</div>
                <span className={cn("text-xs", step >= n ? "font-medium" : "text-muted-foreground")}>
                  {[isEdit ? "Details" : "Details", "Compose", "Examples & submit"][n - 1]}
                </span>
                {n < 3 && <div className="h-px flex-1 bg-border" />}
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 text-sm">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Template name</label>
                <input
                  className={inputClass}
                  placeholder="e.g. order_confirmation"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                  disabled={isEdit}
                />
                <p className="mt-1 text-xs text-muted-foreground">Lowercase letters, numbers, and underscores only.</p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className={labelClass}>WABA</label>
                  <select className={selectClass} value={wabaId} onChange={(e) => setWabaId(e.target.value)} disabled={isEdit}>
                    <option value="">Select...</option>
                    {wabas.map((w) => (<option key={w.id} value={w.id}>{w.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Category</label>
                  <select className={selectClass} value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">Select...</option>
                    {CATEGORIES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Language</label>
                  <select className={selectClass} value={language} onChange={(e) => setLanguage(e.target.value)}>
                    {LANGUAGES.map((l) => (<option key={l.value} value={l.value}>{l.label}</option>))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Header (optional)</label>
                  <select className={selectClass} value={headerType} onChange={(e) => setHeaderType(e.target.value as HeaderType)}>
                    <option value="none">None</option>
                    <option value="text">Text</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="document">Document</option>
                  </select>
                  {headerType === "text" && (
                    <div className="mt-2">
                      <input className={inputClass} placeholder="Header text (max 60 chars). Use {{1}} for variables." value={headerText} onChange={(e) => setHeaderText(e.target.value.slice(0, 60))} />
                      <p className="mt-1 text-xs text-muted-foreground">{headerText.length}/60 characters</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Body <span className="text-destructive">*</span></label>
                  <textarea
                    rows={5}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={"Use {{1}}, {{2}}, etc. for variables.\nExample: Hola {{1}}, tu pedido {{2}} ha sido enviado."}
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value.slice(0, 1024))}
                  />
                  <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{bodyText.length}/1024 characters</span>
                    {bodyVars.length > 0 && <span>Variables: {bodyVars.join(", ")}</span>}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Footer (optional)</label>
                  <input className={inputClass} placeholder="e.g. Reply STOP to unsubscribe" value={footerText} onChange={(e) => setFooterText(e.target.value.slice(0, 60))} />
                  <p className="mt-1 text-xs text-muted-foreground">{footerText.length}/60 characters</p>
                </div>

                <div>
                  <label className={labelClass}>Buttons (optional, max 3)</label>
                  <div className="space-y-2">
                    {buttons.map((btn, i) => (
                      <div key={i} className="rounded-md border border-input p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">
                            {btn.type === "QUICK_REPLY" && "Quick Reply"}
                            {btn.type === "URL" && "Visit Website"}
                            {btn.type === "PHONE_NUMBER" && "Call Phone"}
                          </span>
                          <button onClick={() => removeButton(i)} className="text-muted-foreground hover:text-destructive">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <input className={inputClass} placeholder="Button text (max 25 chars)" value={btn.text} onChange={(e) => updateButton(i, "text", e.target.value.slice(0, 25))} />
                        {btn.type === "QUICK_REPLY" && (
                          <input className={inputClass} placeholder="Button code, payload (max 128 chars)" value={btn.payload || ""} onChange={(e) => updateButton(i, "payload", e.target.value.slice(0, 128))} />
                        )}
                        {btn.type === "URL" && (
                          <input className={inputClass} placeholder="https://example.com/{{1}}" value={btn.url || ""} onChange={(e) => updateButton(i, "url", e.target.value)} />
                        )}
                        {btn.type === "PHONE_NUMBER" && (
                          <input className={inputClass} placeholder="+1234567890" value={btn.phone_number || ""} onChange={(e) => updateButton(i, "phone_number", e.target.value)} />
                        )}
                      </div>
                    ))}
                    {buttons.length < 3 && (
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => addButton("QUICK_REPLY")}>
                          <MessageSquareReply className="h-3.5 w-3.5" /> Quick Reply
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => addButton("URL")}>
                          <Link className="h-3.5 w-3.5" /> URL
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => addButton("PHONE_NUMBER")}>
                          <Phone className="h-3.5 w-3.5" /> Phone
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className={labelClass}>Live preview</label>
                <WhatsAppPreview
                  headerType={headerType} headerText={headerText} bodyText={bodyText}
                  footerText={footerText} buttons={buttons} variableExamples={variableExamples}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                {allVars.length > 0 && (
                  <div>
                    <h4 className="font-medium">Variable examples</h4>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Meta requires example values for each variable to review your template.
                    </p>
                    <div className="mt-3 space-y-2">
                      {allVars.map((v) => (
                        <div key={v.key}>
                          <label className={labelClass}>{v.variable} example</label>
                          <input
                            className={inputClass}
                            placeholder={`Example value for ${v.variable}`}
                            value={variableExamples[v.key] || ""}
                            onChange={(e) => setExample(v.key, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <h4 className="font-medium">Template details</h4>
                <div className="rounded-md border border-border divide-y divide-border text-xs">
                  <div className="flex justify-between px-3 py-2">
                    <span className="text-muted-foreground">Name</span>
                    <span className="font-medium font-mono">{templateName}</span>
                  </div>
                  <div className="flex justify-between px-3 py-2">
                    <span className="text-muted-foreground">Category</span>
                    <span className="font-medium">{CATEGORIES.find((c) => c.value === category)?.label}</span>
                  </div>
                  <div className="flex justify-between px-3 py-2">
                    <span className="text-muted-foreground">Language</span>
                    <span className="font-medium">{LANGUAGES.find((l) => l.value === language)?.label}</span>
                  </div>
                  {!isEdit && (
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-muted-foreground">WABA</span>
                      <span className="font-medium">{wabas.find((w) => String(w.id) === String(wabaId))?.name}</span>
                    </div>
                  )}
                  <div className="flex justify-between px-3 py-2">
                    <span className="text-muted-foreground">Variables</span>
                    <span className="font-medium">{allVars.length}</span>
                  </div>
                  <div className="flex justify-between px-3 py-2">
                    <span className="text-muted-foreground">Buttons</span>
                    <span className="font-medium">{buttons.length}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="mb-2 font-medium">Preview</h4>
                <WhatsAppPreview
                  headerType={headerType} headerText={headerText} bodyText={bodyText}
                  footerText={footerText} buttons={buttons} variableExamples={variableExamples}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border bg-muted/30 px-6 py-3">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={step === 1 || (isEdit && step === 2)} onClick={() => setStep(step - 1)}>Back</Button>
            {step < 3 ? (
              <Button
                size="sm"
                disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid)}
                onClick={() => setStep(step + 1)}
              >
                Next
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleSubmit(false)} disabled={isPending}>
                  {isEdit ? "Save changes" : "Save as draft"}
                </Button>
                <Button size="sm" onClick={() => handleSubmit(true)} disabled={isPending || !step3Valid}>
                  {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  {isEdit && editTemplate?.status !== "draft" ? "Resubmit for review" : "Submit for review"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main page ---
export default function Templates() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"mine" | "gallery">("mine");
  const [wizard, setWizard] = useState(false);
  const [editTemplate, setEditTemplate] = useState<ApiTemplate | null>(null);
  const [viewTemplate, setViewTemplate] = useState<ApiTemplate | null>(null);
  const [deleteTemplate, setDeleteTemplate] = useState<ApiTemplate | null>(null);

  const syncMutation = useMutation({
    mutationFn: () => apiFetch<{ synced: number }>("/api/templates/sync", { method: "POST" }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      alert(`Synced ${result.synced} templates from Meta`);
    },
    onError: (err: Error) => {
      alert(`Sync failed: ${err.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/templates/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setDeleteTemplate(null);
    },
    onError: (err: Error) => {
      alert(`Delete failed: ${err.message}`);
    },
  });

  const { data } = useQuery({
    queryKey: ["templates"],
    queryFn: () => apiFetch<{ templates: ApiTemplate[]; counts: TemplateCounts }>("/api/templates"),
  });

  const templates = data?.templates ?? [];
  const hasReal = templates.length > 0;
  const counts = data?.counts ?? { total: 0, approved: 0, pending: 0, rejected: 0 };

  const handleDuplicate = (t: ApiTemplate) => {
    const parsed = parseComponentsToForm(t.components);
    setEditTemplate(null);
    setWizard(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Templates"
        description="Manage, preview and submit message templates for Meta approval."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
              {syncMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync from Meta
            </Button>
            <Button size="sm" onClick={() => { setEditTemplate(null); setWizard(true); }}>
              <Plus className="h-4 w-4" /> New template
            </Button>
          </>
        }
      />

      {/* Status counts */}
      {hasReal && (
        <div className="flex gap-4 text-xs">
          <span className="text-muted-foreground">Total: <span className="font-medium text-foreground">{counts.total}</span></span>
          <span className="text-muted-foreground">Approved: <span className="font-medium text-green-600">{counts.approved}</span></span>
          <span className="text-muted-foreground">Pending: <span className="font-medium text-yellow-600">{counts.pending}</span></span>
          <span className="text-muted-foreground">Rejected: <span className="font-medium text-red-600">{counts.rejected}</span></span>
        </div>
      )}

      {data && !hasReal && (
        <div className="rounded-lg border border-muted bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
          No templates yet. Click <strong>&quot;Sync from Meta&quot;</strong> to pull your approved templates, or create a new one.
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-border">
        {[
          { id: "mine", label: "My templates" },
          { id: "gallery", label: "Template gallery" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as any)}
            className={cn(
              "border-b-2 px-3 py-2 text-sm font-medium -mb-px transition-colors",
              tab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <FilterBar searchPlaceholder="Search by name or ID..." />

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Category</th>
              <th className="px-4 py-3 text-left font-medium">Language</th>
              <th className="px-4 py-3 text-left font-medium">WABA</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Modified</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {templates.map((t) => (
              <tr
                key={t.id}
                className="hover:bg-muted/40 cursor-pointer"
                onClick={() => setViewTemplate(t)}
              >
                <td className="px-4 py-3">
                  <div className="font-medium">{t.name}</div>
                  {t.body_text && (
                    <div className="mt-0.5 truncate text-xs text-muted-foreground max-w-[200px]">{t.body_text}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">{t.category}</td>
                <td className="px-4 py-3 text-xs">{LANGUAGES.find((l) => l.value === t.language)?.label || t.language}</td>
                <td className="px-4 py-3 text-xs">{t.waba_name}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={t.status} />
                    {t.status === "rejected" && t.rejection_reason && (
                      <span title={t.rejection_reason}>
                        <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(t.updated_at).toLocaleDateString()}</td>
                <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                  <ActionsMenu
                    template={t}
                    onView={() => setViewTemplate(t)}
                    onEdit={() => setEditTemplate(t)}
                    onDelete={() => setDeleteTemplate(t)}
                    onDuplicate={() => handleDuplicate(t)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Wizard (create or edit) */}
      {(wizard || editTemplate) && (
        <TemplateWizard
          onClose={() => { setWizard(false); setEditTemplate(null); }}
          editTemplate={editTemplate}
        />
      )}

      {/* Detail drawer */}
      {viewTemplate && (
        <TemplateDetailDrawer
          template={viewTemplate}
          onClose={() => setViewTemplate(null)}
          onEdit={() => { setViewTemplate(null); setEditTemplate(viewTemplate); }}
        />
      )}

      {/* Delete confirmation */}
      {deleteTemplate && (
        <DeleteConfirm
          template={deleteTemplate}
          onClose={() => setDeleteTemplate(null)}
          onConfirm={() => deleteMutation.mutate(deleteTemplate.id)}
          isPending={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
